// screens/ReviewScreen.js — Reviewer queue: walk through every pending photo,
// approve or reject with annotations and pin-comments.
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { SyncBar, useSmartBack } from '../components/shared';
import PhotoViewer from '../components/PhotoViewer';
import { BottomNav } from './HomeScreen';

export default function ReviewScreen() {
  const { user } = useAuth();
  const goBack = useSmartBack();
  const toast = useToast();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState(''); // '' = all factories
  const [orderFilter, setOrderFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = siteFilter ? `?site_id=${encodeURIComponent(siteFilter)}` : '';
      const { data } = await api.get(`/api/photos/pending-review${params}`);
      setPhotos(data);
    } catch (err) {
      // Avoid re-triggering on every render; one log is enough.
      console.error('Review queue load failed', err);
    } finally {
      setLoading(false);
    }
  }, [siteFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/api/sites').then(({ data }) => setSites(data)).catch(() => {});
  }, []);

  // Order-number filtering happens client-side for instant typing.
  const visiblePhotos = orderFilter.trim()
    ? photos.filter((p) => (p.order_number || '').toLowerCase().includes(orderFilter.trim().toLowerCase()))
    : photos;

  const handleReviewChange = (photoId) => {
    setPhotos((curr) => curr.filter((p) => p.id !== photoId));
    setActiveIdx((idx) => {
      if (idx == null) return null;
      const newLength = visiblePhotos.length - 1;
      if (newLength === 0) return null;
      return Math.min(idx, newLength - 1);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SyncBar />
      <div style={headerStyle}>
        <button className="btn btn-ghost btn-icon" onClick={goBack}>←</button>
        <div>
          <h2 style={{ margin: 0 }}>Review queue</h2>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {user?.full_name} · {visiblePhotos.length} pending photo{visiblePhotos.length === 1 ? '' : 's'}
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      {/* Filters: by factory and by order number */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 16px', flexWrap: 'wrap', borderBottom: '1px solid var(--border-subtle)' }}>
        <select className="input-field" style={{ width: 'auto', minWidth: 180 }}
          value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
          <option value="">All factories</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input className="input-field" style={{ flex: 1, minWidth: 160 }}
          placeholder="Filter by order number…" value={orderFilter}
          onChange={(e) => setOrderFilter(e.target.value)} />
      </div>

      <div className="page-content">
        {loading && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
        {!loading && visiblePhotos.length === 0 && (
          <div style={emptyStyle}>
            <div style={{ fontSize: '3rem', marginBottom: 8 }}>🎉</div>
            <h3 style={{ margin: 0 }}>Inbox zero</h3>
            <p style={{ color: 'var(--text-muted)' }}>Geen foto's meer in afwachting van review.</p>
          </div>
        )}

        {!loading && visiblePhotos.length > 0 && (
          <div style={gridStyle}>
            {visiblePhotos.map((p, idx) => (
              <button key={p.id} onClick={() => setActiveIdx(idx)} style={cardBtnStyle}>
                <img
                  src={p.server_path || ''}
                  alt={p.step_name}
                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                />
                <div style={cardMetaStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.order_number}</div>
                    {p.site_name && <span className="badge badge-secondary" style={{ fontSize: '0.6rem', flexShrink: 0 }}>🏭 {p.site_name}</span>}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {p.product_name} · {p.step_name}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    {p.taken_by_name || '—'} · {new Date(p.taken_at).toLocaleString()}
                    {p.annotation_count > 0 && <span style={{ marginLeft: 6 }}>· 💬 {p.annotation_count}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {activeIdx != null && visiblePhotos[activeIdx] && (
        <PhotoViewer
          photo={visiblePhotos[activeIdx]}
          photos={visiblePhotos}
          onClose={() => setActiveIdx(null)}
          onReviewChange={(photoId) => handleReviewChange(photoId)}
        />
      )}

      <BottomNav active="review" />
    </div>
  );
}

const headerStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)',
};

const emptyStyle = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  textAlign: 'center', padding: '60px 24px', color: 'var(--text-primary)',
};

const gridStyle = {
  display: 'grid', gap: 12,
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
};

const cardBtnStyle = {
  background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)',
  borderRadius: 8, overflow: 'hidden', cursor: 'pointer', padding: 0,
  textAlign: 'left', color: 'var(--text-primary)',
};

const cardMetaStyle = { padding: 10 };
